// Shared domain model — the app's core vocabulary (segments, dataset shapes,
// targeting primitives). Lifted to lib/ so features reference one neutral
// module instead of importing each other for these types (the audience, thread,
// segment, and distribution features all build on this). Framework-agnostic by
// the lib/** layer rule: no React / Mantine / query imports.

export const DatasetType = {
    // Discriminant values match the backend `dataset_type` (StrEnum in
    // services/datasets/domain.py). The backend vocabulary migration renamed
    // `standard`/`sparse` -> `user_attribute` and `custom`/`dense` -> `transaction`.
    USER_ATTRIBUTE: 'user_attribute',
    TRANSACTION: 'transaction',
    // A crossed segment is the intersection of a transaction definition and a
    // user-attribute definition.
    CROSSED: 'crossed',
} as const
export type DatasetType = (typeof DatasetType)[keyof typeof DatasetType]

// Dataset metadata as surfaced in `AgentState.datasets`. Mirrors the shared
// backend `BaseDataset` fields (services/datasets/domain.py); the per-kind
// subclasses add fields the UI doesn't consume, and the backend rejects client
// patches to `datasets`, so only the shared shape is modeled here.
export interface Dataset {
    id: string
    dataset_type: DatasetType
    name: string
    description: string
}

export interface BaseAudienceConfiguration {
    dataset_type: DatasetType
    dataset_id: string
    segment_id: string
}

// --- Targeting primitives (mirror the backend models) ---

// A single field/value condition, e.g. { field: 'mom_teen_hh', values: ['true'] }.
// Backend `FieldValue` (services/audiences/domain.py).
export interface FieldValue {
    field: string
    values: string[]
}

// Boolean grouping of field conditions (backend `TransactionDefinition`). The
// building block of transaction behaviors. (User attributes use the parallel
// `AttributeGroup` / `UserAttributesDefinition` pair below, not this.)
export interface TransactionDefinition {
    must: FieldValue[]
    should: FieldValue[]
    must_not: FieldValue[]
}

// One transaction behavior pattern (backend `UserTransactionBehavior`). The
// `description` is a human-readable summary of the targeting criteria.
export interface UserTransactionBehavior {
    description: string
    must: TransactionDefinition[]
    should: TransactionDefinition[]
    must_not: TransactionDefinition[]
}

// --- User-attribute targeting primitives ---
// The attribute side mirrors the transaction side's two levels, but with its own
// models (the backend keeps them separate so each automatic-function-calling tool
// exposes one concrete request type):
//   BooleanFieldValue  ≈ FieldValue (plus `negate`)   — the leaf predicate
//   AttributeGroup     ≈ TransactionDefinition         — a group of leaves
//   UserAttributesDefinition ≈ UserTransactionBehavior — groups of groups

// A single attribute predicate (backend `BooleanFieldValue`): `field` IN `values`,
// optionally negated. Like a transaction `FieldValue` but carries `negate` — the
// negation rides the literal, so a NOT-IN predicate can sit inside an OR/should
// group, not only in `must_not`.
export interface BooleanFieldValue {
    field: string
    values: string[]
    negate?: boolean
}

// One boolean group of attribute filters (backend `AttributeGroup`): the inner
// level of a `UserAttributesDefinition`. Reads per user as
// (AND of must) ∧ (OR of should) ∧ ¬(OR of must_not).
export interface AttributeGroup {
    must: BooleanFieldValue[]
    should: BooleanFieldValue[]
    must_not: BooleanFieldValue[]
}

// Two-level boolean query over user attributes (backend `UserAttributesDefinition`):
// must (AND) / should (OR) / must_not (NOR) of `AttributeGroup`. Fixed depth
// (groups of filters, never groups of groups) yet propositionally complete.
export interface UserAttributesDefinition {
    must: AttributeGroup[]
    should: AttributeGroup[]
    must_not: AttributeGroup[]
}

// User-attribute segment — backend `UserAttributeAudienceConfiguration`.
export interface UserAttributeAudienceConfiguration extends BaseAudienceConfiguration {
    dataset_type: typeof DatasetType.USER_ATTRIBUTE
    // Required (backend `name: str` / `description: str`): the agent must provide
    // both. `name` is the card title, `description` its subtitle.
    name: string
    description: string
    audience_size: number
    users_with_attributes?: UserAttributesDefinition
}

// Transaction segment — backend `TransactionAudienceConfiguration`
// (`users_with_transactions`).
export interface TransactionAudienceConfiguration extends BaseAudienceConfiguration {
    dataset_type: typeof DatasetType.TRANSACTION
    // Required (backend `name: str` / `description: str`): the agent must provide
    // both. `name` is the card title, `description` its subtitle.
    name: string
    description: string
    audience_size: number
    users_with_transactions?: UserTransactionBehavior[]
}

// Crossed segment — backend `CrossedAudienceConfiguration`, which subclasses the
// transaction config: it carries a transaction definition (`users_with_transactions`)
// intersected with a user-attribute definition (`users_with_attributes`). The
// definitions default server-side (empty list / empty definition), so both are
// optional here to match. A cross spans two datasets: the inherited `dataset_id`
// is the transaction side; `users_with_attributes_dataset_id` is the
// user-attribute side (required, like its server counterpart).
export interface CrossedAudienceConfiguration extends BaseAudienceConfiguration {
    dataset_type: typeof DatasetType.CROSSED
    // Required (backend `name: str` / `description: str`): the agent must provide
    // both. `name` is the card title, `description` its subtitle.
    name: string
    description: string
    audience_size: number
    users_with_transactions?: UserTransactionBehavior[]
    users_with_attributes_dataset_id: string
    // Plain-English summary of the attribute side of the cross (the
    // `users_with_attributes` selection). The transaction side describes itself
    // per `UserTransactionBehavior.description`; the attribute side has no such
    // per-item note, so the cross carries one. Required (the agent provides it).
    users_with_attributes_description: string
    users_with_attributes?: UserAttributesDefinition
}

export type AudienceConfiguration =
    | TransactionAudienceConfiguration
    | UserAttributeAudienceConfiguration
    | CrossedAudienceConfiguration

// A thread surfaced as an Audience (v1: one Audience per thread = its cart of
// segments). View-model the inventory screen reads, mapped from the thread list
// in `src/hooks/useAudienceInventory.ts`.
export interface AudienceThread {
    threadId: string
    name: string
    lastUpdated: string
    segments: AudienceConfiguration[]
    // The thread's available datasets (from AgentState.datasets). Carried so the
    // shared SegmentDetail can offer the user-attribute datasets as expansion
    // (lookalike) modeling targets, even when opened from the inventory screens.
    datasets: Dataset[]
}
